const ENTITY_PROFILE_DEFAULT_FIELD_NAMES = [
  "entity_name",
  "entity_type_name",
  "entity_overall_status",
  "compliance_status",
];

const ENTITY_STATUS_SUMMARY_DEFAULT_FIELD_NAMES = [
  "entity_type_name",
  "entity_overall_status",
  "entity_count",
];

const QUESTION_RESPONSE_SUMMARY_DEFAULT_FIELD_NAMES = [
  "entity_type_name",
  "form_name",
  "section_name",
  "question_text",
  "answered_entity_count",
  "selected_answer",
  "selected_entity_count",
];

const QUESTION_STATISTICS_SUMMARY_DEFAULT_FIELD_NAMES = [
  "entity_type_name",
  "form_name",
  "section_name",
  "question_text",
  "metric_name",
  "selected_answer",
  "metric_value",
];

const ENTITY_PROFILE_FIELD_NAMES = [
  "entity_id",
  "entity_name",
  "entity_type_id",
  "entity_type_name",
  "entity_overall_status",
  "answered_status",
  "answer_date",
  "compliance_status",
];

const ENTITY_STATUS_SUMMARY_FIELD_NAMES = [
  "entity_type_id",
  "entity_type_name",
  "entity_overall_status",
  "entity_count",
];

const QUESTION_RESPONSE_SUMMARY_FIELD_NAMES = [
  "entity_type_id",
  "entity_type_name",
  "form_id",
  "form_name",
  "section_id",
  "section_name",
  "section_question_id",
  "question_text",
  "answered_entity_count",
  "answer_occurrence_count",
  "selected_answer",
  "selected_entity_count",
];

const QUESTION_STATISTICS_SUMMARY_FIELD_NAMES = [
  "entity_type_id",
  "entity_type_name",
  "form_id",
  "form_name",
  "section_id",
  "section_name",
  "section_question_id",
  "question_text",
  "metric_name",
  "selected_answer",
  "metric_value",
];

const QUESTION_SCOPE_REPORT_TYPES = [
  "entity_profile_detail",
  "question_response_summary",
  "question_statistics_summary",
];

const DATE_DIMENSION_NAME_MAP = {
  entity_profile_detail: ["entity_onboarded_date", "answer_date"],
  compliance_summary: ["entity_onboarded_date", "answer_date"],
  entity_status_summary: [
    "entity_onboarded_date",
    "status_transition_date",
  ],
  question_response_summary: ["entity_onboarded_date", "answer_date"],
  question_statistics_summary: ["entity_onboarded_date", "answer_date"],
};

const REPORT_FIELD_NAME_MAP = {
  entity_profile_detail: ENTITY_PROFILE_FIELD_NAMES,
  entity_status_summary: ENTITY_STATUS_SUMMARY_FIELD_NAMES,
  question_response_summary: QUESTION_RESPONSE_SUMMARY_FIELD_NAMES,
  question_statistics_summary: QUESTION_STATISTICS_SUMMARY_FIELD_NAMES,
};

const DEFAULT_FIELD_NAME_MAP = {
  entity_profile_detail: ENTITY_PROFILE_DEFAULT_FIELD_NAMES,
  entity_status_summary: ENTITY_STATUS_SUMMARY_DEFAULT_FIELD_NAMES,
  question_response_summary: QUESTION_RESPONSE_SUMMARY_DEFAULT_FIELD_NAMES,
  question_statistics_summary: QUESTION_STATISTICS_SUMMARY_DEFAULT_FIELD_NAMES,
};

const SELECT_TYPES = [
  "single_select",
  "multi_select",
  "radio",
  "checkboxes",
];
const MULTI_VALUE_SELECT_TYPES = ["multi_select", "checkboxes"];
const QUESTION_METRIC_NAME_MAP = {
  number: [
    "answered_entity_count",
    "answer_occurrence_count",
    "sum",
    "average",
  ],
  single_select: [
    "answered_entity_count",
    "answer_occurrence_count",
    "most_selected_option",
  ],
  multi_select: [
    "answered_entity_count",
    "answer_occurrence_count",
    "most_selected_option",
  ],
  radio: [
    "answered_entity_count",
    "answer_occurrence_count",
    "most_selected_option",
  ],
  checkboxes: [
    "answered_entity_count",
    "answer_occurrence_count",
    "most_selected_option",
  ],
};

export const get_default_report_field_names = (report_type_internal_name) => [
  ...(Reflect.get(DEFAULT_FIELD_NAME_MAP, report_type_internal_name) || []),
];

export const is_report_field_allowed = (
  report_type_internal_name,
  internal_name
) => (
  Reflect.get(REPORT_FIELD_NAME_MAP, report_type_internal_name) || []
).includes(internal_name);

export const is_question_scope_report_type = (report_type_internal_name) =>
  QUESTION_SCOPE_REPORT_TYPES.includes(report_type_internal_name);

export const get_allowed_date_dimension_names = (
  report_type_internal_name
) => [...(Reflect.get(DATE_DIMENSION_NAME_MAP, report_type_internal_name) || [])];

export const format_report_question_type = (question_type) =>
  String(question_type || "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const is_select_question = (question) =>
  SELECT_TYPES.includes(String(question?.question_type || "").toLowerCase());

export const is_multi_select_question = (question) =>
  MULTI_VALUE_SELECT_TYPES.includes(
    String(question?.question_type || "").toLowerCase()
  );

export const get_allowed_metric_names = (question_type) => (
  Reflect.get(
    QUESTION_METRIC_NAME_MAP,
    String(question_type || "").toLowerCase()
  ) || [
    "answered_entity_count",
    "answer_occurrence_count",
  ]
);

export const is_metric_allowed_for_question = (
  question,
  metric_internal_name
) => get_allowed_metric_names(question?.question_type).includes(metric_internal_name);

export const get_default_metric_names = (question_type) =>
  get_allowed_metric_names(question_type).filter(
    (metric_name) => metric_name === "answered_entity_count"
  );

export const get_question_filter_values = (question) => {
  if (!is_select_question(question)) {
    return [];
  }
  const selected_values = question.selected_values || [];
  if (selected_values.length) {
    return selected_values.filter(Boolean);
  }
  return [question.selected_value].filter(Boolean);
};

export const has_question_filter_values = (question) =>
  get_question_filter_values(question).length > 0;

export const build_report_columns = ({
  selected_field_ids,
  required_field_id,
  field_display_labels = {},
}) => {
  const report_field_ids = required_field_id && !selected_field_ids.includes(required_field_id)
    ? [required_field_id, ...selected_field_ids].filter(Boolean)
    : selected_field_ids;
  return report_field_ids.map((report_field_id, index) => ({
    report_field_id,
    display_label:
      String(Reflect.get(field_display_labels, report_field_id) || "").trim() || null,
    order_index: index + 1,
  }));
};

export const build_question_scope_filters = ({
  selected_questions,
  filter_type_id,
}) =>
  selected_questions.map((question, index) => ({
    filter_type_id,
    section_question_id: question.id,
    order_index: index + 1,
  }));

export const build_question_answer_filters = ({
  selected_questions,
  filter_type_id,
}) => {
  const question_offset = selected_questions.length;
  return selected_questions
    .filter((question) => has_question_filter_values(question))
    .map((question, index) => ({
      filter_type_id,
      section_question_id: question.id,
      filter_value: { values: get_question_filter_values(question) },
      order_index: question_offset + index + 1,
    }));
};

export const build_question_metric_rows = ({ selected_questions }) => {
  let order_index = 1;
  return selected_questions.flatMap((question) =>
    (question.selected_metric_type_ids || []).map((metric_type_id) => ({
      section_question_id: question.id,
      metric_type_id,
      order_index: order_index++,
    }))
  );
};

export const build_one_off_template_name = (
  entity_name,
  report_type_label = "Entity Report"
) => {
  const stamp = new Date().toISOString().replace(/[-:TZ.]/g, "").slice(0, 17);
  const normalized_label = String(report_type_label || "Entity Report").trim();
  const base_name = entity_name
    ? `${entity_name} ${normalized_label}`
    : `One-off ${normalized_label}`;
  return `${base_name} ${stamp}`;
};

export const extract_report_builder_error_message = (error) => {
  const response_data = error?.response?.data;
  if (!response_data) {
    return error?.message || "Failed to generate report.";
  }
  if (typeof response_data.message === "string") {
    return response_data.message;
  }
  if (typeof response_data.error_message === "string") {
    return response_data.error_message;
  }
  if (response_data.report?.error_message) {
    return response_data.report.error_message;
  }
  return JSON.stringify(response_data);
};

export const get_template_answer_filter_map = (filters) =>
  (filters || []).reduce((accumulator, filter_row) => {
    if (filter_row.filter_type?.internal_name !== "section_question_answer_any_of") {
      return accumulator;
    }
    Reflect.set(
      accumulator,
      filter_row.section_question_id,
      filter_row.filter_value?.values || []
    );
    return accumulator;
  }, {});

export const get_template_question_metric_map = (question_metrics) =>
  (question_metrics || []).reduce((accumulator, question_metric) => {
    const metric_ids =
      Reflect.get(accumulator, question_metric.section_question_id) || [];
    Reflect.set(accumulator, question_metric.section_question_id, [
      ...metric_ids,
      question_metric.metric_type?.id,
    ].filter(Boolean));
    return accumulator;
  }, {});

const get_question_property = (question, property_name, fallback = "") => {
  const value = Reflect.get(question, property_name);
  return value || fallback;
};

const get_related_property = (
  source_object,
  relation_name,
  property_name,
  fallback = ""
) => {
  const related_object = Reflect.get(source_object, relation_name) || {};
  return get_question_property(related_object, property_name, fallback);
};

const build_selected_question_from_filter = (
  filter_row,
  answer_filter_map,
  question_metric_map
) => {
  const question = filter_row.section_question || {};
  const question_values =
    Reflect.get(answer_filter_map, filter_row.section_question_id) || [];
  const selected_question = {
    id: filter_row.section_question_id,
    question_text: get_question_property(question, "question_text", "Question"),
    question_type: get_question_property(question, "question_type"),
    form_name: get_related_property(question, "form", "form_name"),
    section_name: get_related_property(question, "section", "name"),
    entity_type_ids: [],
    choices: get_question_property(question, "choices", []),
    selected_value: question_values[0] || "",
    selected_values: question_values,
    selected_metric_type_ids:
      Reflect.get(question_metric_map, filter_row.section_question_id) || [],
  };
  return selected_question;
};

export const build_selected_questions_from_template = (
  filters,
  question_metrics = []
) => {
  const answer_filter_map = get_template_answer_filter_map(filters);
  const question_metric_map = get_template_question_metric_map(question_metrics);
  return (filters || [])
    .filter((filter_row) => filter_row.filter_type?.internal_name === "section_question_any_of")
    .map((filter_row) =>
      build_selected_question_from_filter(
        filter_row,
        answer_filter_map,
        question_metric_map
      )
    );
};
